import {
    amountBlock,
    type FinishLoanApplication,
    firstBlock,
    secondBlock,
    thirdBlock,
    type FinishApplicant,
    amountBlockCli,
    initialValues,
    FinishLoanApplicationCleaned,
    firstBlockCoAplicant,
} from './fieldDefinitions';
import { type TFunction } from 'i18next';
import { AccomodationStatusValue, EmploymentStatusCode } from '@n2-common/uiFieldValues/applicationFormFields';
import { getConsentFieldDef } from '@n2-common/uiFieldValues/consents';
import { CliFieldConfig } from '../AmountVisualizer';
import { isGuid } from '@n2-common/lib';

export const CONFIG = {
    // eslint-disable-next-line camelcase
    goalId_ApplicationFlexiLoanSent: '{22FA85DA-874D-466E-A0F0-0F1A87B3E399}',
    // eslint-disable-next-line camelcase
    goalId_ApplicationAnnuityLoanSent: '{00292AC1-78DF-46D9-8989-E1DFF7275695}',
    minimumAge: 23,
    maximumAge: 200,
    modulo: 100,
    minRefinanceAmount: 999,
    maxLoanAmount: 50000,
    minLoanAmount: 1000,
    annAge: 60,
    annAmount: 35000,
    coApplicantName: null as string | null,
};
export type ConfigType = typeof CONFIG;

export function getWizardStagesCl(t: TFunction, config: ConfigType = CONFIG) {
    return [
        amountBlock(t, config),
        firstBlock(t, false),
        secondBlock(t, 'mainApplicant'),
        thirdBlock(t, 'mainApplicant', false),
        //        FirstBlockCoAplicant(t),
        //        secondBlock(t, 'CoApplicant'),
        //        thirdBlock(t, 'CoApplicant'),
        {
            legend: 'Morrow Bank - Luoton ehdot',
            sectionNameForTracking: 'confirm',
            fields: [
                getConsentFieldDef<FinishLoanApplication>('MKT_1_PROFILING_1', t, 'FI', 'consents'),
                getConsentFieldDef<FinishLoanApplication>('MKT_2_COOP_1', t, 'FI', 'consents'),
                getConsentFieldDef<FinishLoanApplication>('MKT_3_COMM_1', t, 'FI', 'consents'),
                getConsentFieldDef<FinishLoanApplication>('ConfirmSA', t, 'FI', 'consents'),
            ],
        },
    ];
}
export function getWizardStagesCli(t: TFunction, config?: CliFieldConfig) {
    if (!config) {
        throw new Error('CliFieldConfig is required');
    }
    return [
        amountBlockCli(t, config),
        firstBlock(t, true),
        secondBlock(t, 'mainApplicant'),
        thirdBlock(t, 'mainApplicant', true),
        ...(config.coApplicantName //
            ? [firstBlockCoAplicant(t), secondBlock(t, 'coApplicant'), thirdBlock(t, 'coApplicant', true)]
            : []),
        {
            legend: 'Morrow Bank - Luoton ehdot',
            sectionNameForTracking: 'confirm',
            fields: [
                getConsentFieldDef<FinishLoanApplication>('MKT_1_PROFILING_1', t, 'FI', 'consents'),
                getConsentFieldDef<FinishLoanApplication>('MKT_2_COOP_1', t, 'FI', 'consents'),
                getConsentFieldDef<FinishLoanApplication>('MKT_3_COMM_1', t, 'FI', 'consents'),
                getConsentFieldDef<FinishLoanApplication>('ConfirmSA', t, 'FI', 'consents'),
            ],
        },
    ];
}

export function cleanupFormValuesForSubmitting(values: FinishLoanApplication, config: ConfigType = CONFIG) {
    const cleaned: FinishLoanApplicationCleaned = {
        ...values,
        refinanceDebt: hideRefinanceDebt(values.desiredCredit, config) ? false : values.refinanceDebt,
        mainApplicant: cleanupApplicantData(values.mainApplicant),
        coApplicant: values.hasCoApplicant && values.coApplicant ? cleanupApplicantData(values.coApplicant) : null,
        consents: Object.entries(values.consents).reduce((m, [key, value]) => (value ? [...m, key] : m), [] as string[]),
    };

    return cleaned;
}

export function cleanupFormValuesForSubmittingCli(values: FinishLoanApplication): FinishLoanApplicationCleaned {
    values =
        values &&
        Object.entries(values).reduce((acc, [key, value]) => (key in initialValues ? { ...acc, [key]: value } : acc), {} as FinishLoanApplication);
    if (!isGuid(values.coSignerGpid)) {
        delete values.coSignerGpid;
    } else console.log(values.coSignerGpid);
    return {
        ...values,
        mainApplicant: cleanupApplicantData(values.mainApplicant),
        coApplicant: values.hasCoApplicant && values.coApplicant ? cleanupApplicantData(values.coApplicant) : null,
        consents: Object.entries(values.consents).reduce((m, [key, value]) => (value ? [...m, key] : m), [] as string[]),
    };
}

function cleanupApplicantData(applicant: FinishApplicant) {
    const cleaned = Object.entries(applicant).reduce(
        (m, [key, value]) => ({ ...m, [key]: value != null ? value.toString() : null }),
        {} as typeof applicant
    );
    if (!specifyAccomodationCost(cleaned.accommodationType)) {
        cleaned.accommodationCost = '';
    }

    return cleaned;
}

export function specifyAccommodationTypeOther(accommodationType: AccomodationStatusValue | '' | null | undefined) {
    if (!accommodationType || accommodationType === null) {
        return false;
    }
    return accommodationType === 'O';
}

export function specifyAccomodationCost(accommodationType: AccomodationStatusValue | '' | null | undefined) {
    if (accommodationType === '' || !accommodationType || accommodationType === null) {
        return false;
    }
    return accommodationType !== 'H';
}

export function specifyEmployerName(employmentStatus: EmploymentStatusCode | '' | null | undefined) {
    if (!employmentStatus || employmentStatus === null) {
        return false;
    }
    return employmentStatus === 'F' || employmentStatus === 'I';
}

export function hideRefinanceDebt(desiredCredit: string, config: ConfigType) {
    return parseInt(desiredCredit, 10) <= config.minRefinanceAmount;
}
